/* Mechanics constants.
 *
 * Every number here is owned by 02-functional-design.md (the mechanics authority)
 * or by a drift resolution in 10 §3.3. They are restated once, in one place, so
 * the server cannot drift from the client or the tests. Changing any of them is a
 * change request, not an edit.
 */
/** Summit altitude in metres. The mountain is always Everest-scaled (02 §2.5). */
export const SUMMIT_ALTITUDE_M = 8848;
/** Quiz / Camp / Summit pass bar — a scoring rule, never a content field. */
export const PASS_SCORE = 0.8;
/** Rubric review: pass = weighted mean >= threshold AND no criterion at 0 (DR-09, A4). */
export const DEFAULT_RUBRIC_PASS_THRESHOLD = 2.0;
export const RUBRIC_MIN_SCORE = 0;
export const RUBRIC_MAX_SCORE = 3;
/** Calibration (02 §2.3.3, DR-04). A Domain with 19 windowed items is never flagged. */
export const CALIBRATION_WINDOW_DAYS = 90;
export const OVERCONFIDENCE_FLAG_GAP = 0.15;
export const OVERCONFIDENCE_MIN_SAMPLE = 20;
export const CALIBRATION_PULSE_ITEM_COUNT = 4;
/** Erosion thresholds with hysteresis to stop flag flapping (02 A2, DR-05). */
export const EROSION_RETENTION_SET_BELOW = 0.7;
export const EROSION_RETENTION_CLEAR_AT = 0.8;
export const EROSION_QUIZ_AGE_DAYS = 90;
/** A Camp/Summit attempt refreshes a Topic's quiz age only with >= 3 of its items (C16). */
export const QUIZ_AGE_REFRESH_MIN_ITEMS = 3;
/** Drills (02 §2.2, §6.8). */
export const DEFAULT_NEW_CARD_CAP = 8;
export const NEW_CARD_CAP_MIN = 0;
export const NEW_CARD_CAP_MAX = 20;
export const DRILL_ROUND_SIZE = 20;
export const MAX_EXERCISE_CARDS_PER_ROUND = 2;
/** "Again" re-entry: after >= 10 intervening cards OR >= 10 minutes, whichever first. */
export const AGAIN_REENTRY_MIN_CARDS = 10;
export const AGAIN_REENTRY_MIN_MS = 10 * 60 * 1000;
/** Backlog auto-pause: due > max(3 x trailingAverage, 5 x cap) (02 A16 / C8). */
export const BACKLOG_PAUSE_TRAILING_MULTIPLIER = 3;
export const BACKLOG_PAUSE_CAP_FLOOR_MULTIPLIER = 5;
export const TRAILING_AVERAGE_WINDOW_DAYS = 14;
/** Rhythm (02 §6.1, 04 §6.6). Breaks only after 2 consecutive non-qualifying days. */
export const WEEKLY_GOAL_DAYS = 5;
export const STREAK_MAX_GAP_DAYS = 2;
/** FSRS (ADR-05, 04 A6). Fuzz is disabled inside the fold — always. */
export const DEFAULT_DESIRED_RETENTION = 0.9;
export const DEFAULT_FSRS_PARAMS_VERSION = 'default-v1';
export const FSRS_GRADES = { again: 1, hard: 2, good: 3, easy: 4 };
/** Time (02 §6.9, 04 §1.4, DR-13). Two DIFFERENT clamps — do not merge them. */
export const TIMEZONE = 'Europe/Amsterdam';
/** Fold-order clamp: occurred_at more than +5 min ahead is clamped to receipt. */
export const FUTURE_SKEW_CLAMP_MINUTES = 5;
/** Day-attribution clamp: a separate +24 h rule for streaks/caps/nudges. */
export const DAY_ATTRIBUTION_CLAMP_HOURS = 24;
/** Content release (03 A5 / DR-14, 05 §7.1). */
export const RETAINED_RELEASES_ON_DISK = 5;
export const RELEASE_UPLOAD_MAX_BYTES = 200 * 1024 * 1024;
/** Backups (03 A6 / DR-15). */
export const BACKUP_DAILY_RETENTION = 14;
export const BACKUP_WEEKLY_RETENTION = 8;
export const BACKUP_FRESHNESS_MAX_HOURS = 26;
/** Acknowledged events stay in the client archive this long for epoch replay (A16). */
export const CLIENT_EVENT_ARCHIVE_DAYS = 14;
/** Push (02 §5, DR-16). Quiet hours are the half-open local interval [22:00, 08:00). */
export const QUIET_HOURS_START = 22;
export const QUIET_HOURS_END = 8;
export const MAX_PUSHES_PER_DAY = 2;
export const DEFAULT_NUDGE_TIME = '18:00';
export const DIGEST_TIME = '08:00';
export const DEFAULT_DEEP_BLOCK_DAY = 'saturday';
/** Tutor (02 §2.9, 03 §6.3). */
export const TUTOR_THREAD_HISTORY_MESSAGES = 10;
export const TUTOR_MAX_MESSAGE_BYTES = 8192;
export const TUTOR_SSE_KEEPALIVE_MS = 15_000;
export const TUTOR_DIGEST_MAX_ERODING_TOPICS = 10;
export const TUTOR_DIGEST_RECENT_QUIZZES = 5;
/** Attempts (02 §6.12, A18). Resumable 24 h, then discarded unscored. */
export const ATTEMPT_RESUME_WINDOW_HOURS = 24;
/** Code exercises (02 §2.8). Wall-clock cutoff, client-side. */
export const CODE_RUN_TIMEOUT_MS = 10_000;
/* Self-hosted WASM runtimes (11 §1.6, 03 A10). Pinned here so the vendoring
 * script, the loader, the Settings pack UI and the service worker cannot drift
 * from one another; they must match `client/package.json` exactly. */
export const PYODIDE_PACK_VERSION = '314.0.3';
export const SQLITE_PACK_VERSION = '3.53.0-build1';
export const PYODIDE_BASE = `/runtime/pyodide/${PYODIDE_PACK_VERSION}/`;
export const SQLITE_BASE = `/runtime/sqlite-wasm/${SQLITE_PACK_VERSION}/`;
/** Cache Storage bucket the offline pack lives in. Also the Workbox route name,
 * so an explicit download and an offline load hit the same entries. */
export const PYODIDE_CACHE_NAME = 'ascent-pyodide-pack';
/** Rate limits (03 §5.3; C32 permits tuning within an order of magnitude). */
export const RATE_LIMITS = {
    enroll: { max: 5, windowMs: 60 * 60 * 1000 },
    device: { max: 120, windowMs: 60 * 1000 },
    tutorMessages: { max: 30, windowMs: 60 * 60 * 1000 },
    admin: { max: 10, windowMs: 60 * 60 * 1000 },
    publicUnknown: { max: 60, windowMs: 60 * 1000 },
};
/** Design tokens (09-brief-opus-build.md §5) — binding from M1, not a repaint at M6. */
export const DESIGN_TOKENS = {
    pageBackground: '#0d0d0d',
    surface: '#1a1a19',
    inkPrimary: '#ffffff',
    inkSecondary: '#c3c2b7',
    inkMuted: '#898781',
    hairline: 'rgba(255,255,255,0.10)',
    cluster: {
        C1: '#3987e5',
        C2: '#d95926',
        C3: '#199e70',
        C4: '#c98500',
        C5: '#d55181',
    },
    status: { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b' },
};
//# sourceMappingURL=constants.js.map